<template>
  <div>
    <v-skeleton-loader
      v-if="isLoading"
      type="card, card, table"
    />

    <div
      v-else
      class="pb-15"
    >
      <!-- 日付範囲フィルター -->
      <v-card class="mb-4">
        <v-card-text>
          <v-row align="center">
            <v-col
              cols="12"
              sm="4"
            >
              <v-menu
                v-model="startDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                min-width="auto"
              >
                <template #activator="{ on, attrs }">
                  <v-text-field
                    v-model="startDate"
                    label="開始日"
                    :prepend-icon="icons.mdiCalendar"
                    readonly
                    dense
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="startDate"
                  locale="ja"
                  :day-format="date => new Date(date).getDate()"
                  color="primary"
                  no-title
                  @input="startDateMenu = false"
                />
              </v-menu>
            </v-col>
            <v-col
              cols="12"
              sm="4"
            >
              <v-menu
                v-model="endDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                min-width="auto"
              >
                <template #activator="{ on, attrs }">
                  <v-text-field
                    v-model="endDate"
                    label="終了日"
                    :prepend-icon="icons.mdiCalendar"
                    readonly
                    dense
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="endDate"
                  locale="ja"
                  :day-format="date => new Date(date).getDate()"
                  color="primary"
                  no-title
                  @input="endDateMenu = false"
                />
              </v-menu>
            </v-col>
            <v-col
              cols="12"
              sm="4"
            >
              <v-btn
                color="primary"
                :ripple="false"
                @click="fetchCustomers"
              >
                絞り込み
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- サマリーカード -->
      <v-row class="mb-4">
        <v-col
          cols="6"
          sm="3"
        >
          <v-card>
            <v-card-text class="text-center">
              <div class="text-subtitle-2 text--secondary">
                顧客数
              </div>
              <div class="text-h5">
                {{ summary.customerCount }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col
          cols="6"
          sm="3"
        >
          <v-card>
            <v-card-text class="text-center">
              <div class="text-subtitle-2 text--secondary">
                総来店数
              </div>
              <div class="text-h5">
                {{ summary.totalVisitCount }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col
          cols="6"
          sm="3"
        >
          <v-card>
            <v-card-text class="text-center">
              <div class="text-subtitle-2 text--secondary">
                総購入金額（大計）
              </div>
              <div class="text-h5">
                {{ formatCurrency(summary.totalPurchaseAmountWithTax) }}
              </div>
              <div class="text-caption text--secondary mt-1">
                小計: {{ formatCurrency(summary.totalPurchaseAmount) }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col
          cols="6"
          sm="3"
        >
          <v-card>
            <v-card-text class="text-center">
              <div class="text-subtitle-2 text--secondary">
                平均購入金額（大計）
              </div>
              <div class="text-h5">
                {{ formatCurrency(summary.averagePurchasePerCustomerWithTax) }}
              </div>
              <div class="text-caption text--secondary mt-1">
                小計: {{ formatCurrency(summary.averagePurchasePerCustomer) }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- 高単価顧客分析（権限がある場合のみ表示） -->
      <v-card
        v-if="canViewCustomerStats"
        class="mb-4"
      >
        <v-card-title>高単価顧客分析</v-card-title>
        <v-card-text>
          <v-radio-group
            v-model="analysisType"
            row
            class="mt-0 mb-2"
          >
            <v-radio
              label="総購入金額で分析"
              value="total"
            />
            <v-radio
              label="来店単価で分析"
              value="perVisit"
            />
          </v-radio-group>
          <v-row align="center">
            <v-col
              cols="12"
              sm="8"
            >
              <v-text-field
                v-if="analysisType === 'total'"
                v-model.number="totalThreshold"
                label="総購入金額閾値 (円)"
                type="number"
                dense
                hint="この金額以上の顧客を高単価と判定"
                persistent-hint
              />
              <v-text-field
                v-else
                v-model.number="perVisitThreshold"
                label="来店単価閾値 (円)"
                type="number"
                dense
                hint="1回あたりの来店単価がこの金額以上の顧客を高単価と判定"
                persistent-hint
              />
            </v-col>
            <v-col
              cols="12"
              sm="4"
            >
              <v-btn
                color="secondary"
                :ripple="false"
                :loading="isAnalyzing"
                @click="analyzeHighValue"
              >
                分析する
              </v-btn>
            </v-col>
          </v-row>

          <!-- 分析結果 -->
          <div
            v-if="analysisResult"
            class="mt-6"
          >
            <v-row>
              <v-col
                cols="12"
                md="6"
              >
                <!-- 円グラフ -->
                <div
                  class="chart-container"
                  style="max-width: 300px; margin: 0 auto;"
                >
                  <chartjs-component-doughnut-chart
                    v-if="chartData"
                    :key="chartKey"
                    :data="chartData"
                    :options="chartOptions"
                  />
                </div>
              </v-col>
              <v-col
                cols="12"
                md="6"
              >
                <!-- 分析結果カード -->
                <v-simple-table dense>
                  <template #default>
                    <thead>
                      <tr>
                        <th></th>
                        <th class="text-right">
                          高単価顧客
                        </th>
                        <th class="text-right">
                          一般顧客
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>顧客数</td>
                        <td class="text-right">
                          {{ analysisResult.summary.highValueCustomerCount }}
                        </td>
                        <td class="text-right">
                          {{ analysisResult.summary.regularCustomerCount }}
                        </td>
                      </tr>
                      <tr>
                        <td>売上</td>
                        <td class="text-right">
                          {{ formatCurrency(analysisResult.summary.highValueSales) }}
                        </td>
                        <td class="text-right">
                          {{ formatCurrency(analysisResult.summary.regularSales) }}
                        </td>
                      </tr>
                      <tr>
                        <td>売上貢献率</td>
                        <td class="text-right">
                          {{ analysisResult.summary.highValueContributionRatio }}%
                        </td>
                        <td class="text-right">
                          {{ analysisResult.summary.regularContributionRatio }}%
                        </td>
                      </tr>
                      <tr>
                        <td>平均購入金額</td>
                        <td class="text-right">
                          {{ formatCurrency(analysisResult.summary.highValueAveragePerCustomer) }}
                        </td>
                        <td class="text-right">
                          {{ formatCurrency(analysisResult.summary.regularAveragePerCustomer) }}
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-col>
            </v-row>
          </div>
        </v-card-text>
      </v-card>

      <!-- 顧客ランキング・離反リスク -->
      <v-row
        v-if="canViewCustomerStats"
        class="mb-4"
      >
        <v-col
          cols="12"
          md="6"
        >
          <customer-ranking
            :start-date="startDate"
            :end-date="endDate"
            @customer-click="goToCustomerById"
          />
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <churn-risk-alert @customer-click="goToCustomerById" />
        </v-col>
      </v-row>

      <!-- RFM分析・来店パターン -->
      <v-row
        v-if="canViewCustomerStats"
        class="mb-4"
      >
        <v-col
          cols="12"
          md="6"
        >
          <rfm-analysis
            :date-range="{ start: startDate, end: endDate }"
            @customer-click="goToCustomerById"
          />
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <visit-pattern :date-range="{ start: startDate, end: endDate }" />
        </v-col>
      </v-row>

      <!-- 検索エリア -->
      <div class="d-flex align-center mb-4">
        <v-text-field
          v-model="searchQuery"
          :prepend-inner-icon="icons.mdiMagnify"
          label="名前・会員番号で検索"
          single-line
          hide-details
          clearable
          dense
          outlined
          style="max-width: 300px;"
          class="mr-2"
          @keyup.enter="handleSearch"
          @click:clear="handleClearSearch"
        />
        <v-btn
          color="secondary"
          :loading="isSearching"
          @click="handleSearch"
        >
          検索
        </v-btn>
      </div>

      <!-- 顧客テーブル -->
      <v-card>
        <v-card-title>
          <span>顧客一覧</span>
        </v-card-title>
        <v-card-text>
          <v-data-table
            :headers="headers"
            :items="customers"
            :items-per-page="15"
            no-data-text="データがありません"
            @click:row="goToCustomer"
          >
            <template #[`item.totalPurchaseAmount`]="{ item }">
              <div>{{ formatCurrency(item.totalPurchaseAmountWithTax) }}</div>
              <div class="text-caption text--secondary">
                {{ formatCurrency(item.totalPurchaseAmount) }}
              </div>
            </template>
            <template #[`item.lastVisitedAt`]="{ item }">
              {{ formatDate(item.lastVisitedAt) }}
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script>
import {
  ref,
  reactive,
  computed,
  watch,
  getCurrentInstance,
} from '@vue/composition-api'
import { mdiCalendar, mdiMagnify } from '@mdi/js'
import { format, startOfMonth, parseISO } from 'date-fns'
import CustomerApi from '@/api/waiter/Customer'
import ChartjsComponentDoughnutChart from '@/views/components/charts/ChartjsComponentDoughnutChart.vue'
import useAbilityCheck from '@/views/composable/useAbilityCheck'
import CustomerRanking from '@/views/v2-temp/common/customer-management/components/CustomerRanking.vue'
import ChurnRiskAlert from '@/views/v2-temp/common/customer-management/components/ChurnRiskAlert.vue'
import RfmAnalysis from '@/views/v2-temp/common/customer-management/components/RfmAnalysis.vue'
import VisitPattern from '@/views/v2-temp/common/customer-management/components/VisitPattern.vue'

export default {
  components: {
    ChartjsComponentDoughnutChart,
    CustomerRanking,
    ChurnRiskAlert,
    RfmAnalysis,
    VisitPattern,
  },
  setup() {
    const vm = getCurrentInstance().proxy
    const { canViewCustomerStats } = useAbilityCheck()

    // ローディング状態
    const isLoading = ref(false)
    const isAnalyzing = ref(false)
    const isSearching = ref(false)

    // 日付フィルター
    const startDateMenu = ref(false)
    const endDateMenu = ref(false)
    const startDate = ref(format(startOfMonth(new Date()), 'yyyy-MM-dd'))
    const endDate = ref(format(new Date(), 'yyyy-MM-dd'))

    // データ
    const customers = ref([])
    const summary = reactive({
      customerCount: 0,
      totalVisitCount: 0,
      totalPurchaseAmount: 0,
      totalPurchaseAmountWithTax: 0,
      averagePurchasePerCustomer: 0,
      averagePurchasePerCustomerWithTax: 0,
    })

    // 検索フィルター
    const searchQuery = ref('')

    // 高単価分析
    const analysisType = ref('total')
    const totalThreshold = ref(null)
    const perVisitThreshold = ref(null)
    const analysisResult = ref(null)

    // 分析タイプ切り替え時に未使用のフィールドをクリア
    watch(analysisType, newType => {
      if (newType === 'total') {
        perVisitThreshold.value = null
      } else {
        totalThreshold.value = null
      }
    })

    // テーブルヘッダー
    const headers = [
      { text: '会員番号', value: 'memberNumber', width: '100px' },
      { text: '名前', value: 'name' },
      { text: '来店回数', value: 'visitCount', align: 'right' },
      { text: '購入金額（大計/小計）', value: 'totalPurchaseAmount', align: 'right' },
      { text: '最終来店日', value: 'lastVisitedAt' },
    ]

    // グラフデータ
    const chartData = computed(() => {
      if (!analysisResult.value?.summary) return null

      return {
        labels: ['高単価顧客', '一般顧客'],
        datasets: [
          {
            data: [
              analysisResult.value.summary.highValueSales,
              analysisResult.value.summary.regularSales,
            ],
            backgroundColor: ['#4CAF50', '#9E9E9E'],
            borderWidth: 0,
          },
        ],
      }
    })

    // チャートの再レンダリング用キー
    const chartKey = computed(() => {
      if (!analysisResult.value?.summary) return 0

      return `${analysisResult.value.summary.highValueSales}-${analysisResult.value.summary.regularSales}`
    })

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: true,
      legend: {
        position: 'bottom',
      },
    }

    // フォーマット関数
    const formatCurrency = value => {
      if (value == null) return '-'

      return new Intl.NumberFormat('ja-JP', {
        style: 'currency',
        currency: 'JPY',
        maximumFractionDigits: 0,
      }).format(value)
    }

    const formatDate = value => {
      if (!value) return '-'

      return format(parseISO(value), 'yyyy/MM/dd')
    }

    // API呼び出し
    const fetchCustomers = async (query = null) => {
      isLoading.value = true

      try {
        const res = await CustomerApi.getCustomersWithStats({
          startDate: startDate.value,
          endDate: endDate.value,
          q: query,
        })

        if (res?.data) {
          customers.value = res.data.customers || []
          Object.assign(summary, res.data.summary || {})
        }
      } catch (error) {
        vm.$toast.error('顧客データの取得に失敗しました')
      } finally {
        isLoading.value = false
      }
    }

    // 検索
    const handleSearch = async () => {
      const query = searchQuery.value?.trim()
      if (!query) {
        await fetchCustomers()

        return
      }

      isSearching.value = true
      try {
        const res = await CustomerApi.getCustomersWithStats({
          startDate: startDate.value,
          endDate: endDate.value,
          q: query,
        })

        if (res?.data) {
          customers.value = res.data.customers || []
          Object.assign(summary, res.data.summary || {})
        }
      } catch (error) {
        vm.$toast.error('検索に失敗しました')
      } finally {
        isSearching.value = false
      }
    }

    const handleClearSearch = () => {
      searchQuery.value = ''
      fetchCustomers()
    }

    const analyzeHighValue = async () => {
      const threshold = analysisType.value === 'total' ? totalThreshold.value : perVisitThreshold.value
      if (!threshold) {
        vm.$toast.error('閾値を指定してください')

        return
      }

      isAnalyzing.value = true

      try {
        const res = await CustomerApi.getHighValueAnalysis({
          startDate: startDate.value,
          endDate: endDate.value,
          totalThreshold: totalThreshold.value,
          perVisitThreshold: perVisitThreshold.value,
        })

        if (res?.data) {
          analysisResult.value = res.data
        }
      } catch (error) {
        vm.$toast.error('分析に失敗しました')
      } finally {
        isAnalyzing.value = false
      }
    }

    const goToCustomer = item => {
      vm.$router.push({
        name: 'admin-customer-stats-show',
        params: { customerId: item.id },
      })
    }

    const goToCustomerById = customerId => {
      vm.$router.push({
        name: 'admin-customer-stats-show',
        params: { customerId },
      })
    }

    // 初期データ取得
    fetchCustomers()

    return {
      // data
      isLoading,
      isAnalyzing,
      isSearching,
      startDateMenu,
      endDateMenu,
      startDate,
      endDate,
      customers,
      searchQuery,
      summary,
      analysisType,
      totalThreshold,
      perVisitThreshold,
      analysisResult,
      headers,
      chartData,
      chartKey,
      chartOptions,
      canViewCustomerStats,

      // methods
      formatCurrency,
      formatDate,
      fetchCustomers,
      handleSearch,
      handleClearSearch,
      analyzeHighValue,
      goToCustomer,
      goToCustomerById,

      icons: {
        mdiCalendar,
        mdiMagnify,
      },
    }
  },
}
</script>

<style scoped>
.chart-container {
  position: relative;
}
</style>
